import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Import tab screens
import PhotosTabScreen from './PD_PhotosTabScreen';
import PersonalTabScreen from './PD_PersonalTabScreen';
import EducationTabScreen from './PD_EducationTabScreen';
import FamilyTabScreen from './PD_FamilyTabScreen';
import ContactTabScreen from './PD_ContactTabScreen';
import PreferencesTabScreen from './PD_PreferencesTabScreen';
import MoreInfoTabScreen from './PD_MoreInfoTabScreen';

const ProfileDetailScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { memberId, isSharedProfile } = route.params;
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('photos');
  const [shortProfile, setShortProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentTabEditState, setCurrentTabEditState] = useState({
    isEditing: false,
    hasUnsavedChanges: false,
    onSave: null,
    onCancel: null,
  });
  const [pendingTab, setPendingTab] = useState(null);
  const [educationRefreshKey, setEducationRefreshKey] = useState(0);

  // Check if viewing own profile
  const isOwnProfile = user?.memberId === memberId;
  const isMediatorProfile = user?.userType === 'Mediator';

  useEffect(() => {
    loadProfileData();
  }, [memberId]);

  // Refresh education tab when returning from EditEducationScreen
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // Only refresh if we're on ProfileDetail with a valid memberId
      if (memberId) {
        setEducationRefreshKey(prev => prev + 1);
      }
    });
    return unsubscribe;
  }, [navigation, memberId]);

  const loadProfileData = async () => {
    // Guard against undefined memberId (safety check)
    if (!memberId) {
      console.warn('ProfileDetailScreen: memberId is undefined, skipping load');
      return;
    }
    
    setIsLoading(true);
    try {
      const shortProfileResponse = await api.get(`/api/EnhancedMember/shortProfile/${memberId}?includePhotos=true`);
      const shortProfileValue = shortProfileResponse.data?.data || shortProfileResponse.data;
      setShortProfile(shortProfileValue);
    } catch (error) {
      console.error('Error loading profile:', error);
      Alert.alert('Error', 'Failed to load profile details');
      navigation.goBack();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataChange = () => {
    setHasChanges(true);
  };

  const handleEditStateChange = (editState) => {
    setCurrentTabEditState(editState);
  };

  const handleTabSwitch = (newTab) => {
    if (currentTabEditState.isEditing && currentTabEditState.hasUnsavedChanges) {
      setPendingTab(newTab);
      
      // Use platform-specific confirmation
      if (Platform.OS === 'web') {
        // Web: Use custom confirmation pattern with window methods
        const userChoice = window.confirm(
          'You have unsaved changes. Click OK to discard changes and switch tabs, or Cancel to stay on this tab.\n\nTo save your changes, click Cancel and then click the Save button.'
        );
        
        if (userChoice) {
          // User clicked OK - discard changes
          if (currentTabEditState.onCancel) {
            currentTabEditState.onCancel();
          }
          setActiveTab(newTab);
          setPendingTab(null);
          setCurrentTabEditState({
            isEditing: false,
            hasUnsavedChanges: false,
            onSave: null,
            onCancel: null,
          });
        } else {
          // User clicked Cancel - stay on current tab
          setPendingTab(null);
        }
      } else {
        // Native: Use Alert.alert with three buttons
        Alert.alert(
          'Unsaved Changes',
          'You have unsaved changes. What would you like to do?',
          [
            {
              text: 'Cancel',
              style: 'cancel',
              onPress: () => setPendingTab(null),
            },
            {
              text: 'Discard',
              style: 'destructive',
              onPress: () => {
                if (currentTabEditState.onCancel) {
                  currentTabEditState.onCancel();
                }
                setActiveTab(newTab);
                setPendingTab(null);
                setCurrentTabEditState({
                  isEditing: false,
                  hasUnsavedChanges: false,
                  onSave: null,
                  onCancel: null,
                });
              },
            },
            {
              text: 'Save',
              onPress: async () => {
                if (currentTabEditState.onSave) {
                  const saved = await currentTabEditState.onSave();
                  if (saved) {
                    setActiveTab(newTab);
                    setPendingTab(null);
                    setCurrentTabEditState({
                      isEditing: false,
                      hasUnsavedChanges: false,
                      onSave: null,
                      onCancel: null,
                    });
                  } else {
                    setPendingTab(null);
                  }
                }
              },
            },
          ],
          { cancelable: false }
        );
      }
    } else {
      setActiveTab(newTab);
    }
  };

  const handleGoBack = () => {
    // Determine the correct return screen based on who is viewing
    const returnScreen = route.params?.returnScreen || (isOwnProfile ? 'MyProfile' : 'MemberProfile');
    
    if (hasChanges) {
      // Signal the previous screen to reload by setting a param
      // For MyProfile (own profile), don't pass memberId to keep proper context
      const navParams = returnScreen === 'MyProfile' 
        ? { shouldRefresh: true }
        : { memberId, shouldRefresh: true };
      
      navigation.navigate({
        name: returnScreen,
        params: navParams,
        merge: true,
      });
    } else {
      // Always navigate to the correct screen to ensure proper context
      // For MyProfile (own profile), don't pass memberId
      if (returnScreen === 'MyProfile') {
        navigation.navigate(returnScreen);
      } else {
        navigation.navigate(returnScreen, { memberId });
      }
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const renderBottomNav = () => {
    // Filter out Contact tab if viewing a shared profile AND user is a Match Seeker (not Mediator)
    const allTabs = [
      { id: 'photos', label: 'Photos', icon: 'images' },
      { id: 'personal', label: 'Personal', icon: 'person' },
      { id: 'education', label: 'Education', icon: 'school' },
      { id: 'family', label: 'Family', icon: 'people' },
      { id: 'contact', label: 'Contact', icon: 'call' },
      { id: 'preferences', label: 'Preferences', icon: 'heart' },
      { id: 'more', label: 'More', icon: 'information-circle' },
    ];
    
    const visibleTabs = (isSharedProfile && !isMediatorProfile)
      ? allTabs.filter(tab => tab.id !== 'contact')
      : allTabs;
    
    return (
      <View style={styles.bottomNavContainer}>
        {visibleTabs.map((tab) => (
        <TouchableOpacity
          key={tab.id}
          style={styles.bottomNavButton}
          onPress={() => handleTabSwitch(tab.id)}
        >
          <SvgIcon 
            name={tab.icon} 
            size={24} 
            color={activeTab === tab.id ? colors.accent : '#D1D5DB'} 
          />
          <Text style={[
            styles.bottomNavLabel,
            activeTab === tab.id && styles.bottomNavLabelActive
          ]}>
            {tab.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'photos':
        return <PhotosTabScreen 
          shortProfile={shortProfile} 
          onPhotoChange={() => { loadProfileData(); handleDataChange(); }} 
          isOwnProfile={isOwnProfile}
          isMediatorProfile={isMediatorProfile}
        />;
      case 'personal':
        return <PersonalTabScreen 
          memberId={memberId} 
          isOwnProfile={isOwnProfile} 
          isMediatorProfile={isMediatorProfile} 
          onEditStateChange={handleEditStateChange}
        />;
      case 'education':
        return <EducationTabScreen 
          key={educationRefreshKey}
          memberId={memberId} 
          isOwnProfile={isOwnProfile} 
          isMediatorProfile={isMediatorProfile} 
          onEditStateChange={handleEditStateChange}
        />;
      case 'family':
        return <FamilyTabScreen 
          memberId={memberId} 
          isOwnProfile={isOwnProfile} 
          isMediatorProfile={isMediatorProfile} 
          onEditStateChange={handleEditStateChange}
        />;
      case 'contact':
        return <ContactTabScreen 
          memberId={memberId} 
          isOwnProfile={isOwnProfile} 
          isMediatorProfile={isMediatorProfile} 
          onEditStateChange={handleEditStateChange}
        />;
      case 'preferences':
        return <PreferencesTabScreen 
          memberId={memberId} 
          isOwnProfile={isOwnProfile} 
          isMediatorProfile={isMediatorProfile} 
          onEditStateChange={handleEditStateChange}
        />;
      case 'more':
        return <MoreInfoTabScreen 
          memberId={memberId} 
          isOwnProfile={isOwnProfile} 
          isMediatorProfile={isMediatorProfile} 
          onEditStateChange={handleEditStateChange}
        />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={styles.loadingText}>Loading profile...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        {shortProfile && (
          <View style={styles.headerMainContent}>
            <View style={styles.headerProfileInfo}>
              <Text style={styles.headerProfileName}>
                {shortProfile.name} {shortProfile.surName}
              </Text>
              <View style={styles.headerInfoRow}>
                <Text style={styles.headerInfoText}>
                  {calculateAge(shortProfile.dateOfBirth)} years
                </Text>
                <Text style={styles.headerInfoText}> • </Text>
                <Text style={styles.headerInfoText}>
                  {shortProfile.profession || 'Not Specified'}
                </Text>
                <Text style={styles.headerInfoText}> • </Text>
                <Text style={styles.headerInfoText}>
                  {shortProfile.highestEducationQualification || 'N/A'}
                </Text>
              </View>
              <View style={styles.headerInfoRow}>
                <SvgIcon name="location-outline" size={14} color={colors.accent} />
                <Text style={styles.headerInfoText}>
                  {shortProfile.city}{shortProfile.state ? `, ${shortProfile.state}` : ''}{shortProfile.country ? `, ${shortProfile.country}` : ''}
                </Text>
              </View>
            </View>
            
            <TouchableOpacity onPress={handleGoBack} style={styles.backButton}>
              <SvgIcon name="arrow-back" size={28} color={colors.white} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Tab Content */}
      <View style={styles.contentContainer}>
        {renderTabContent()}
      </View>
      
      {/* Bottom Navigation */}
      {renderBottomNav()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  header: {
    paddingHorizontal: 0,
    paddingTop: 15,
    paddingBottom: 15,
    backgroundColor: colors.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.secondary,
  },
  headerMainContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'relative',
    paddingHorizontal: 20,
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    padding: 8,
    paddingLeft: 15,
  },
  headerProfileInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerProfileName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 8,
    textAlign: 'center',
  },
  headerInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
    justifyContent: 'center',
  },
  headerInfoText: {
    fontSize: 14,
    color: '#E0E0E0',
  },
  contentContainer: {
    position: 'absolute',
    top: 110,
    left: 0,
    right: 0,
    bottom: 80,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  loadingText: {
    color: colors.white,
    marginTop: 10,
    fontSize: 16,
  },
  bottomNavContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    backgroundColor: colors.primary,
    borderTopWidth: 1,
    borderTopColor: colors.secondary,
    paddingTop: 8,
    paddingBottom: 12,
    paddingHorizontal: 4,
    justifyContent: 'space-around',
    zIndex: 9999,
    elevation: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  bottomNavButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
  },
  bottomNavLabel: {
    fontSize: 11,
    color: '#D1D5DB',
    marginTop: 4,
    textAlign: 'center',
  },
  bottomNavLabelActive: {
    color: colors.accent,
    fontWeight: '600',
  },
});

export default ProfileDetailScreen;
